/** @format */

import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { useGetSubjectsQuery } from "../app/services/subjectApi";
import { useLocale } from "./hooks";
import { locales } from "./init";

const switchLocaleInURI = (URI: string, newLocale: string): string => {
  return URI.replace(new RegExp(`^/(?:en|cy)`), `/${newLocale}`);
};

const translateSubjectInURI = (
  URI: string,
  oldSubject: string,
  newSubject: string,
): string => {
  return URI.replace(encodeURI(oldSubject), encodeURI(newSubject));
};

/**
 * if subject translation is needed
 */
const LocaleSwitchWithSubjects: React.FC<{
  className?: string;
  subjectParam: string;
}> = ({ className, subjectParam }) => {
  const { data: subjects, isLoading: isLoadingSubjects } =
    useGetSubjectsQuery();
  const { i18n } = useTranslation();
  const currentLocale = useLocale();
  const { pathname } = useLocation();

  const newSubject = subjects?.subjects.find(
    (subject) => subject.title[currentLocale] === subjectParam,
  );

  return isLoadingSubjects && !subjects ? (
    <p
      className={`navbar-nav nav LocaleSwitch${className ? ` ${className}` : ""}`}
    >
      ...
    </p>
  ) : newSubject ? (
    <ul
      className={`navbar-nav nav LocaleSwitch${className ? ` ${className}` : ""}`}
    >
      {locales.map(
        (locale) =>
          i18n.resolvedLanguage !== locale && (
            <li key={locale} className="nav-item">
              {newSubject && (
                <Link
                  to={translateSubjectInURI(
                    switchLocaleInURI(pathname, locale),
                    subjectParam,
                    newSubject.title[locale],
                  )}
                  key={locale}
                  className="nav-link border-0 bg-transparent"
                >
                  {i18n.getFixedT(locale)("localeName")}
                </Link>
              )}
            </li>
          ),
      )}
    </ul>
  ) : (
    // If the subject is found as undefined then use the switch that doesn't translate
    <LocaleSwitchWithoutSubjects className={className} />
  );
};

/**
 * if no subject translation is needed
 */
const LocaleSwitchWithoutSubjects: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  return (
    <ul
      className={`navbar-nav nav LocaleSwitch${className ? ` ${className}` : ""}`}
    >
      {locales.map(
        (locale) =>
          i18n.resolvedLanguage !== locale && (
            <li key={locale} className="nav-item">
              <Link
                to={switchLocaleInURI(pathname, locale)}
                key={locale}
                className="nav-link border-0 bg-transparent"
              >
                {i18n.getFixedT(locale)("localeName")}
              </Link>
            </li>
          ),
      )}
    </ul>
  );
};

/**
 * A wrapper to decide whether to load a subjects list or not
 */
const LocaleSwitch: React.FC<{ className?: string }> = ({ className }) => {
  const { subject } = useParams();

  if (subject) {
    return (
      <LocaleSwitchWithSubjects className={className} subjectParam={subject} />
    );
  } else {
    return <LocaleSwitchWithoutSubjects className={className} />;
  }
};

export default LocaleSwitch;
